import { isDefinedString, isPositiveNumber, reduce } from "et3-type-guards";

import AiGeneratedBadge from "../../../../../components/ai-generated-badge";
import { EApprovalDecision } from "../../../../../constants/enum/approval-decision";
import { EApprovalDescriptionStatus } from "../../../../../constants/enum/approval-description-status";
import { EApprovalItemStatus } from "../../../../../constants/enum/approval-item-status";
import i18n, { Ti18nKeys } from "../../../../../constants/i18n";
import Et3Button from "../../../../../core/et3-button";
import ET3Div from "../../../../../core/et3-div";
import { withLiteObserverAndTheme } from "../../../../../core/et3-enhanced-render";
import Et3Text from "../../../../../core/et3-text";
import { checkIfHasBadWordsAndSocialMedia } from "../../../../../helpers/et3-text-checker/social-media-and-bad-words";
import getApproveRejectStore from "../../../../../stores/approval-reject-store";
import getUserAdvancedStore from "../../../../../stores/user-store/advanced-store";
import { TDescriptionType } from "../../../../../Types";
import ApprovalStatusChip from "../../ApprovalStatusChip";
import LaneHeader from "./LaneHeader";
import {
  DESCRIPTION_FONT_SIZE,
  DESCRIPTION_LINE_HEIGHT,
  DESCRIPTION_TEXT_HEIGHT,
  LANE_FOOTER_GAP,
  LANE_FOOTER_PADDING_TOP
} from "./lane-constants";

const getDescriptionStatus = (descriptionStatus: string) => {
  switch (descriptionStatus) {
    case EApprovalDescriptionStatus.APPROVED:
      return EApprovalItemStatus.APPROVED;
    case EApprovalDescriptionStatus.PENDING:
      return EApprovalItemStatus.PENDING;
    default:
      return EApprovalItemStatus.REJECTED;
  }
};

const DescriptionLane = withLiteObserverAndTheme<{
  titleKey: Ti18nKeys;
  isSelfDescription: boolean;
  userId: number;
  userUUID: string;
  descriptionId: number;
  descriptionStatus: string;
  description: string;
  descriptionLanguage: string;
  isGeneratedByAi: number;
}>((props) => {
  const {
    titleKey,
    isSelfDescription,
    userId,
    userUUID,
    descriptionId,
    descriptionStatus,
    description,
    descriptionLanguage,
    isGeneratedByAi,
    theme
  } = props;
  const { colors, unitX, unitY } = theme;
  const reducedDescription = reduce(() => description, "");
  const hasDescription = isDefinedString(reducedDescription);
  const status = getDescriptionStatus(reduce(() => `${descriptionStatus}`, ""));
  const isPending = status === EApprovalItemStatus.PENDING;
  const descriptionAlignment =
    getUserAdvancedStore().getDescriptionAlignment(descriptionLanguage);
  const { hasBadWords, hasSocialMedia } =
    checkIfHasBadWordsAndSocialMedia(reducedDescription);
  const textColors = hasBadWords
    ? { backgroundColor: colors.all.red, textColor: colors.all.white }
    : hasSocialMedia
      ? { backgroundColor: colors.all.orange, textColor: colors.all.white }
      : {
          backgroundColor: colors.all.transparent,
          textColor: colors.all.maastrichtBlue
        };

  const descriptionType: TDescriptionType = isSelfDescription
    ? "self_description"
    : "peer_description";
  const isChangeStatusPressed = getApproveRejectStore().getActionStatus(
    descriptionType,
    descriptionId
  );

  const decide = async (decision: EApprovalDecision) => {
    const store = getApproveRejectStore();
    const buttonId = `${userId}-${decision}Description-${descriptionId}`;
    if (decision === EApprovalDecision.APPROVED) {
      await store.approveUserDescription(
        descriptionId,
        userId,
        userUUID,
        "APPROVAL_DATA",
        descriptionType,
        isPending,
        buttonId
      );
    } else {
      await store.rejectUserDescription(
        descriptionId,
        userId,
        userUUID,
        "APPROVAL_DATA",
        descriptionType,
        isPending,
        buttonId
      );
    }
    store.registerDecision();
  };

  return (
    <ET3Div
      isScrollable={false}
      isExtendable={true}
      withShadow={false}
      width={0}
      height={0}
      fDirection="column"
      alignItems="stretch"
      justifyContent="flex-start"
      color={colors.all.transparent}
      style={{ width: "100%", minWidth: 0 }}
      onClick={() => {}}
    >
      <LaneHeader titleKey={titleKey}>
        <AiGeneratedBadge
          isGeneratedByAi={isSelfDescription ? isGeneratedByAi : 0}
        />
        <ET3Div
          isScrollable={false}
          isExtendable={false}
          withShadow={false}
          width={0}
          height={0}
          fDirection="row"
          alignItems="center"
          justifyContent="flex-start"
          color={colors.all.transparent}
          style={{
            flex: 1,
            width: "auto",
            minWidth: 0,
            height: "auto",
            minHeight: 0
          }}
          onClick={() => {}}
        />
        {hasDescription ? (
          <ApprovalStatusChip status={status} />
        ) : (
          <Et3Text
            fontFamily={"LIGHT"}
            fontSize={DESCRIPTION_FONT_SIZE}
            text={i18n.get("NONE_ACTIVE")}
            isSpan={true}
            color={colors.all.gray}
            style={{ margin: 0 }}
          />
        )}
      </LaneHeader>

      <ET3Div
        isScrollable={true}
        isExtendable={false}
        withShadow={false}
        width={0}
        height={DESCRIPTION_TEXT_HEIGHT}
        fDirection="column"
        alignItems="stretch"
        justifyContent="flex-start"
        color={colors.all.transparent}
        style={{ width: "100%", minWidth: 0 }}
        onClick={() => {}}
      >
        <Et3Text
          fontFamily={"LIGHT"}
          fontSize={DESCRIPTION_FONT_SIZE}
          text={
            hasDescription
              ? reducedDescription
              : i18n.get("NO_DESCRIPTION_SUBMITTED")
          }
          isSpan={false}
          color={hasDescription ? textColors.textColor : colors.all.gray}
          style={{
            margin: 0,
            backgroundColor: hasDescription
              ? textColors.backgroundColor
              : colors.all.transparent,
            textAlign: hasDescription ? descriptionAlignment : "left",
            overflowWrap: "break-word",
            lineHeight: DESCRIPTION_LINE_HEIGHT
          }}
        />
      </ET3Div>

      {hasDescription ? (
        <ET3Div
          isScrollable={false}
          isExtendable={false}
          withShadow={false}
          width={0}
          height={0}
          fDirection="row"
          alignItems="center"
          justifyContent="flex-end"
          color={colors.all.transparent}
          style={{
            width: "100%",
            minWidth: 0,
            height: "auto",
            minHeight: 0,
            marginTop: "auto",
            paddingTop: LANE_FOOTER_PADDING_TOP * unitY,
            gap: LANE_FOOTER_GAP * unitX
          }}
          onClick={() => {}}
        >
          {isPending ? (
            <Et3Button
              type="REJECT"
              isEnabled={true}
              size="COMPACT"
              text="REJECT"
              onClick={async () => await decide(EApprovalDecision.REJECTED)}
            />
          ) : null}
          {isPending ? (
            <Et3Button
              type="APPROVE"
              isEnabled={true}
              size="COMPACT"
              text="APPROVE"
              onClick={async () => await decide(EApprovalDecision.APPROVED)}
            />
          ) : null}
          {isPending ? null : (
            <Et3Button
              type="GET"
              isEnabled={!isChangeStatusPressed}
              size="COMPACT"
              text="CHANGE_STATUS"
              onClick={async () => {
                await decide(
                  status === EApprovalItemStatus.APPROVED
                    ? EApprovalDecision.REJECTED
                    : EApprovalDecision.APPROVED
                );
                if (isPositiveNumber(descriptionId)) {
                  getApproveRejectStore().setActionStatus(
                    descriptionType,
                    descriptionId,
                    true
                  );
                }
              }}
            />
          )}
        </ET3Div>
      ) : null}
    </ET3Div>
  );
});
export default DescriptionLane;
